import React, { useRef, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { ChevronRight, ChevronLeft } from 'lucide-react'
import HotIcon from '../../Assets/hoticon.png'
import BDCoinIcon from '../../Assets/icons/coin.png'

function CoinCard({ offer, isHot, onSelect }) {
  if (offer.isCustom) {
    return (
      <motion.button
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        onClick={() => onSelect(offer)}
        className="bg-[#6264654D] w-[130px] sm:w-[145px] h-[120px] sm:h-[127px] rounded-xl p-4 flex items-center justify-center gap-1 cursor-pointer hover:bg-[#333] transition-colors"
      >
        <img src={BDCoinIcon} alt='bd-coin-icon' className="w-[18px] h-[18px] sm:w-[22px] sm:h-[22px]" fill="currentColor" />
        <span className="text-white font-bold text-xs sm:text-sm">Custom</span>
      </motion.button>
    )
  }

  return (
    <motion.button
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      onClick={() => onSelect(offer)}
      className={`relative rounded-xl p-3 flex flex-col items-center justify-center gap-1 cursor-pointer transition-colors bg-[#6264654D] w-[130px] sm:w-[145px] h-[120px] sm:h-[127px] ${isHot ? 'bg-[#35353080] hover:bg-[#333]' : 'bg-[#1a1a1a] hover:bg-[#252525]'
        }`}
    >
      {isHot && (
        <div className="absolute top-0 right-0 text-[#c9a227]">
          <img src={HotIcon} alt='hot-icon' className="w-[18px] h-[18px] sm:w-[21px] sm:h-[21px]" fill="currentColor" />
        </div>
      )}
      <div className="flex items-center gap-1">
        <img src={BDCoinIcon} alt='bd-coin-icon' className="w-[18px] h-[18px] sm:w-[22px] sm:h-[22px]" fill="currentColor" />
        <span className="text-white font-bold text-xs sm:text-sm md:text-base">
          {offer.coins >= 1000 ? `${(offer.coins / 1000).toLocaleString()},000` : offer.coins.toLocaleString()}
        </span>
      </div>
      <span className="text-[9px] sm:text-[10px]">for</span>
      <span className="text-[10px] sm:text-[11px]">{offer.price}</span>
      {isHot && offer.original && (
        <span className="text-gray-300 text-[9px] sm:text-[10px] line-through">{offer.original}</span>
      )}
    </motion.button>
  )
}

function BuyCoinsGrid({ offers, isHot = false, onSelect }) {
  const scrollRef = useRef(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(true)

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 200, behavior: 'smooth' })
    }
  }

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
      setShowLeftArrow(scrollLeft > 0)
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 10)
    }
  }

  useEffect(() => {
    const scrollElement = scrollRef.current
    if (scrollElement) {
      checkScroll()
      scrollElement.addEventListener('scroll', checkScroll)
      window.addEventListener('resize', checkScroll)
      return () => {
        scrollElement.removeEventListener('scroll', checkScroll)
        window.removeEventListener('resize', checkScroll)
      }
    }
  }, [offers])

  if (isHot) {
    return (
      <div className="relative group">
        {/* Left Arrow */}
        {showLeftArrow && (
          <button
            onClick={() => scroll(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-black/50 hover:bg-[#4a4a4a] flex items-center justify-center text-white transition-all shadow-lg backdrop-blur-sm"
          >
            <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        )}
        
        {/* Scrollable Container */}
        <div
          ref={scrollRef}
          className="flex gap-2 sm:gap-3 overflow-x-auto pb-3 scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {offers.map((offer, idx) => (
            <div key={idx} className="flex-shrink-0">
              <CoinCard offer={offer} isHot onSelect={onSelect} />
            </div>
          ))}
        </div>
        
        {/* Right Arrow */}
        {showRightArrow && (
          <button
            onClick={() => scroll(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-black/50 hover:bg-[#4a4a4a] flex items-center justify-center text-white transition-all shadow-lg backdrop-blur-sm"
          >
            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-6 gap-2 sm:gap-3">
      {offers.map((offer, idx) => (
        <CoinCard key={idx} offer={offer} onSelect={onSelect} />
      ))}
    </div>
  )
}

export default BuyCoinsGrid